import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { faAlbumCollection } from '@fortawesome/pro-light-svg-icons/faAlbumCollection';
import { TypedMemo } from '../../common/types';
import { useGetCatalogQuery } from '../../../redux/api/fymAPI';
import { CatalogItem } from '../../../redux/api/fymAPI.types';
import { SMLCreationSheet } from '../../smartLinks/creationSheet/SMLCreationSheet';
import { catalogCreationSource } from '../../smartLinks/catalog/catalogCreationSource';
import { HomeBlock } from './HomeBlock';
import { HomeBlockSkeleton } from './HomeBlockSkeleton';
import { HomeSetupCard } from './HomeSetupCard';
import { CatalogLatestRelease } from './CatalogLatestRelease';
import { CatalogCountsTrio } from './CatalogCountsTrio';
import { CatalogAttentionList } from './CatalogAttentionList';
import { artistHomeMessages as messages } from './artistHomeMessages';
import { catalogAttentionRows, catalogCounts, pickLatestRelease } from './catalogSummary';

interface CatalogBlockProps {
  accountId: number;
  onOpenCatalog: () => void;
}

/**
 * The Catalog block (design a1): the latest release and its smartlink, three counts and up to
 * three rows that need work. An empty catalog shows "Claim your first release" (design b2).
 * "Create smartlink" opens the creation sheet on that release, as the Catalog row does.
 */
const CatalogBlockComponent: React.FC<CatalogBlockProps> = ({ accountId, onOpenCatalog }) => {
  const intl = useIntl();
  const { data, isLoading, error, refetch } = useGetCatalogQuery({ accountId });
  const [createFor, setCreateFor] = useState<CatalogItem>();

  const items = useMemo(() => data?.items ?? [], [data]);
  const now = useMemo(() => new Date(), []);
  const latest = useMemo(() => pickLatestRelease(items, now), [items, now]);
  const counts = useMemo(() => catalogCounts(items, now), [items, now]);
  const attention = useMemo(() => catalogAttentionRows(items, latest, now), [items, latest, now]);
  const creationSource = useMemo(() => catalogCreationSource(createFor), [createFor]);

  const retry = useCallback(() => {
    refetch().catch(console.error);
  }, [refetch]);
  const closeCreate = useCallback(() => setCreateFor(undefined), []);

  const title = intl.formatMessage(messages.catalogTitle);

  if (isLoading) {
    return (
      <HomeBlock icon={faAlbumCollection} title={title}>
        <HomeBlockSkeleton />
      </HomeBlock>
    );
  }

  return (
    <HomeBlock
      icon={faAlbumCollection}
      title={title}
      moreLabel={latest === undefined ? undefined : intl.formatMessage(messages.catalogOpen)}
      onMore={onOpenCatalog}
      error={error}
      retry={retry}
    >
      {latest === undefined ? (
        <HomeSetupCard
          icon={faAlbumCollection}
          title={intl.formatMessage(messages.catalogEmptyTitle)}
          body={intl.formatMessage(messages.catalogEmptyBody)}
          cta={intl.formatMessage(messages.catalogEmptyCta)}
          onAction={onOpenCatalog}
          isBare
        />
      ) : (
        <>
          <CatalogLatestRelease item={latest} onCreateLink={setCreateFor} />
          <CatalogCountsTrio counts={counts} />
          <CatalogAttentionList rows={attention} onFixLinks={onOpenCatalog} onCreateLink={setCreateFor} />
        </>
      )}
      <SMLCreationSheet
        accountId={accountId}
        isOpen={createFor !== undefined}
        closeModal={closeCreate}
        initialCatalogTrackMaid={creationSource.catalogTrackMaid}
        initialCatalogItemUuid={creationSource.catalogItemUuid}
      />
    </HomeBlock>
  );
};

export const CatalogBlock = TypedMemo(CatalogBlockComponent);
